export type AiReadLine = {
  key: string
  family: string
  group: string
  text: string
  /** The day of the founder's record the AI read when it earned the line; null before any update. */
  recordAsOf?: string | null
  /** That record in a few words, for example "deck of 12 Oct, 2 documents". */
  recordSummary?: string | null
}

/** Group order and headings (DRAFT_WORDING_NOT_APPROVED.md, F4; David has not approved them yet). */
export const AI_READ_GROUPS = [
  { group: 'looked_at', heading: "What investors' AI looked at" },
  { group: 'could_not_find', heading: 'What it could not find on your record' },
  { group: 'read_as', heading: 'How it read each area' },
  { group: 'would_bring_back', heading: 'What would bring it back' },
] as const

export type AiReadGroup = { group: string; heading: string; lines: AiReadLine[] }

/** The lines split into the four groups in a fixed order; a group with no lines is left out. */
export function groupAiReadLines(lines: readonly AiReadLine[]): AiReadGroup[] {
  return AI_READ_GROUPS.map(({ group, heading }) => ({
    group,
    heading,
    lines: lines.filter((line) => line.group === group),
  })).filter((entry) => entry.lines.length > 0)
}

export type AiReadSection = {
  /** The record's day, or `before-first-update`. */
  key: string
  /** "Based on your record as of 12 Oct: deck of 12 Oct, 2 documents". */
  heading: string
  groups: AiReadGroup[]
}

const BEFORE_FIRST_UPDATE = 'before-first-update'

const dayOf = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })

/**
 * The lines split by the record the AI read, newest record first (the order the server sends them in), and
 * inside each record into the four groups. A founder reads what an earlier version of the record earned
 * as that, not as a verdict on the record they have now.
 */
export function sectionAiReadLines(lines: readonly AiReadLine[]): AiReadSection[] {
  const order: string[] = []
  const byRecord = new Map<string, AiReadLine[]>()
  for (const line of lines) {
    const key = line.recordAsOf ?? BEFORE_FIRST_UPDATE
    if (!byRecord.has(key)) {
      byRecord.set(key, [])
      order.push(key)
    }
    byRecord.get(key)?.push(line)
  }
  return order.map((key) => {
    const members = byRecord.get(key) ?? []
    const summary = members.find((line) => line.recordSummary)?.recordSummary
    return {
      key,
      heading:
        key === BEFORE_FIRST_UPDATE
          ? 'Based on your record before your first update'
          : `Based on your record as of ${dayOf(key)}${summary ? `: ${summary}` : ''}`,
      groups: groupAiReadLines(members),
    }
  })
}
